<template>
  <el-dialog
    title="兴趣词受众推荐"
    :visible.sync="dialogVisible"
    width="900px"
    :close-on-click-modal="false"
    @open="handleOpen"
    @close="handleClose"
  >
    <!-- 筛选区域 -->
    <div class="flex items-center mb-4">
      <span class="mr-2 text-sm text-[#666]">产品</span>
      <el-tag
        type="info"
        size="medium"
        class="mr-4"
      >
        {{ productName }} (ID: {{ productId }})
      </el-tag>
      <span class="mr-2 text-sm text-[#666]">时间范围</span>
      <el-select
        v-model="timeRange"
        size="small"
        class="!w-[120px] mr-3"
      >
        <el-option
          label="近3个月"
          value="last_3m"
        />
        <el-option
          label="近6个月"
          value="last_6m"
        />
        <el-option
          label="近12个月"
          value="last_12m"
        />
      </el-select>
      <el-button
        type="primary"
        size="small"
        @click="handleQuery"
      >查询</el-button>
    </div>

    <!-- 表格 -->
    <el-table
      ref="table"
      v-loading="loading"
      :data="tableData"
      border
      row-key="interestId"
      @selection-change="handleSelectionChange"
      max-height="400"
    >
      <el-table-column
        type="selection"
        width="50"
        :reserve-selection="true"
        :selectable="checkSelectable"
      />
      <!-- <el-table-column
        label="#"
        width="50"
        type="index"
        :index="getRowIndex"
      /> -->
      <el-table-column
        prop="interestId"
        label="ID"
        width="100"
      />
      <el-table-column
        prop="interestName"
        label="兴趣词名称"
        min-width="200"
        show-overflow-tooltip
      />
      <el-table-column
        prop="conversions"
        label="转化数"
        width="120"
        align="center"
      />
      <el-table-column
        label="转化成本"
        width="120"
        align="center"
      >
        <template slot-scope="{ row }">
          ¥{{ row.conversionCost | formatNumber }}
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页行 -->
    <div class="flex justify-end mt-3">
      <el-pagination
        small
        layout="total, sizes, prev, pager, next"
        :current-page.sync="pagination.pageNum"
        :page-size.sync="pagination.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="pagination.totalNum"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <!-- 操作行 -->
    <div class="flex items-center justify-between mt-3 pt-3 border-t border-[#eee]">
      <div class="text-sm text-[#666]">
        <span>已选 </span>
        <el-tooltip
          placement="top"
          :disabled="selectedRows.length === 0"
        >
          <div slot="content">
            <div
              v-for="item in selectedRows"
              :key="item.interestId"
              class="leading-6"
            >
              {{ item.interestName }}
            </div>
          </div>
          <span class="text-primary font-semibold cursor-pointer">{{ selectedRows.length }}</span>
        </el-tooltip>
        <span> 个兴趣词</span>
      </div>
      <div>
        <el-button
          size="small"
          @click="handleCancel"
        >取消</el-button>
        <el-button
          type="primary"
          size="small"
          :disabled="selectedRows.length === 0"
          @click="handleConfirm"
        >确定</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { interestRecommendList } from '@/api/google/create.js';

export default {
  name: 'RecommendAudience',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: [String, Number],
      default: '',
    },
    productName: {
      type: String,
      default: '',
    },
    interestList: {
      type: Array,
      default: () => [],
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
  },
  filters: {
    formatNumber(val) {
      if (!val && val !== 0) return '';
      return Number(val).toLocaleString();
    },
  },
  data() {
    return {
      timeRange: 'last_3m',
      tableData: [],
      loading: false,
      selectedRows: [],
      pagination: {
        pageNum: 1,
        pageSize: 10,
        totalNum: 0,
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },
    // 已选过的兴趣词 ID 集合（用于禁选）
    disabledIdSet() {
      return new Set(this.selectedIds.map(String));
    },
  },
  methods: {
    // 判断行是否可选（已选过的禁选）
    checkSelectable(row) {
      return !this.disabledIdSet.has(String(row.interestId));
    },

    // 序号计算
    getRowIndex(index) {
      return (this.pagination.pageNum - 1) * this.pagination.pageSize + index + 1;
    },

    // 弹窗打开时
    handleOpen() {
      this.selectedRows = [];
      this.pagination.pageNum = 1;
      this.timeRange = 'last_3m';
      if (this.$refs.table) {
        this.$refs.table.clearSelection();
      }
      this.fetchData();
    },

    // 弹窗关闭时
    handleClose() {
      this.tableData = [];
      this.selectedRows = [];
      if (this.$refs.table) {
        this.$refs.table.clearSelection();
      }
    },

    // 查询
    handleQuery() {
      this.pagination.pageNum = 1;
      this.fetchData();
    },

    // 获取数据
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          productId: this.productId,
          timeRange: this.timeRange,
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize,
        };
        const res = await interestRecommendList(params);
        if (res.code === 0) {
          this.tableData = res.data.list || [];
          this.pagination.totalNum = res.data.totalNum || 0;
        }
      } finally {
        this.loading = false;
      }
    },

    // 选中变化
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },

    // 翻页
    handlePageChange(page) {
      this.pagination.pageNum = page;
      this.fetchData();
    },

    // 切换每页条数
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.pageNum = 1;
      this.fetchData();
    },

    // 递归提取 tree 中所有节点 id
    flattenTreeIds(tree) {
      return tree.reduce((ids, node) => {
        ids.push(String(node.id));
        if (node.children && node.children.length) {
          ids.push(...this.flattenTreeIds(node.children));
        }
        return ids;
      }, []);
    },

    // 确定
    handleConfirm() {
      const allTreeIds = new Set(this.flattenTreeIds(this.interestList));
      // 过滤：只保留在 tree 中存在的词
      const validIds = this.selectedRows
        .map((row) => String(row.interestId))
        .filter((id) => allTreeIds.has(id));
      this.$emit('confirm', validIds);
      this.dialogVisible = false;
    },

    // 取消
    handleCancel() {
      this.dialogVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .el-dialog__body {
  padding-top: 16px;
  padding-bottom: 16px;
}
</style>
